import api from './api';
import { withMockFallback } from './mocks/withMockFallback';
import {
  createZoneMock,
  deleteZoneMock,
  listZonesMock,
  updateZoneMock,
} from './mocks/zonesMock';

/**
 * Зоны камеры со стабильным zone_id (production C3).
 *
 * Чтение доступно пользователю и админу: GET /cameras/{id}/zones, ответ —
 * конверт `{ camera_id, zones }`. Мутации админские: /admin/cameras/{id}/zones.
 * UI продолжает работать с `{ camera_id, items }`, потому что нормализация
 * конверта — задача транспорта, а не компонентов.
 */

const readPath = (cameraId) => `/cameras/${cameraId}/zones`;
const adminPath = (cameraId) => `/admin/cameras/${cameraId}/zones`;

export const normalizeZoneList = (data, cameraId) => ({
  camera_id: data?.camera_id ?? (cameraId == null ? null : Number(cameraId)),
  items: Array.isArray(data?.zones)
    ? data.zones
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data)
        ? data
        : [],
});

export const listZones = (cameraId) =>
  withMockFallback(
    () => api.get(readPath(cameraId)).then((response) => normalizeZoneList(response.data, cameraId)),
    () => listZonesMock(cameraId).then((data) => normalizeZoneList(data, cameraId))
  );

/*
 * ТЗ V5 №6: каталог зон для наложения на кадры кэшируется по camera_id —
 * перелистывание кадров одной камеры не должно слать GET на каждый кадр.
 * Любая мутация зон камеры сбрасывает её запись; неудачный запрос в кэше не
 * остаётся, следующий показ кадра попробует снова.
 */
const zoneCatalogCache = new Map();

export const invalidateZoneCatalog = (cameraId) => {
  if (cameraId == null) zoneCatalogCache.clear();
  else zoneCatalogCache.delete(String(cameraId));
};

export const listZonesCached = (cameraId) => {
  const key = String(cameraId);
  if (!zoneCatalogCache.has(key)) {
    const request = listZones(cameraId).catch((error) => {
      if (zoneCatalogCache.get(key) === request) zoneCatalogCache.delete(key);
      throw error;
    });
    zoneCatalogCache.set(key, request);
  }
  return zoneCatalogCache.get(key);
};

const invalidating = (cameraId) => (data) => {
  invalidateZoneCatalog(cameraId);
  return data;
};

export const createZone = (cameraId, payload) =>
  withMockFallback(
    () => api.post(adminPath(cameraId), payload).then((response) => response.data),
    () => createZoneMock(cameraId, payload)
  ).then(invalidating(cameraId));

/** expected_version защищает чужую правку геометрии: несовпадение → 409. */
export const updateZone = (cameraId, zoneId, payload) =>
  withMockFallback(
    () => api.put(`${adminPath(cameraId)}/${zoneId}`, payload).then((response) => response.data),
    () => updateZoneMock(cameraId, zoneId, payload)
  ).then(invalidating(cameraId));

/**
 * FE-Z06: первый DELETE — без force. На 409 zone_used_by_active_rules backend
 * возвращает dependent_rules; повторный DELETE с ?force=true отправляется
 * только после явного подтверждения пользователя. Без force зона в базе
 * остаётся, и рисовать успех нельзя.
 */
export const deleteZone = (cameraId, zoneId, { expectedVersion = null, force = false } = {}) =>
  withMockFallback(
    () => api
      .delete(`${adminPath(cameraId)}/${zoneId}`, {
        data: expectedVersion == null ? {} : { expected_version: expectedVersion },
        ...(force ? { params: { force: true } } : {}),
      })
      .then((response) => response.data),
    () => deleteZoneMock(cameraId, zoneId, { expectedVersion, force })
  ).then(invalidating(cameraId));

/** Одна зона камеры; 404 пробрасывается как есть — зона могла быть удалена. */
export const getZone = (cameraId, zoneId) =>
  api.get(`${readPath(cameraId)}/${encodeURIComponent(zoneId)}`).then((response) => response.data);
